import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Card, Form } from 'react-bootstrap';
import ActionBar from '@/components/common/ActionBar';
import applicationService from '@/shared/services/applicationService';
import useRevealAlert from '@/shared/hooks/useRevealAlert';
import { EMAIL_PATTERN } from '@/shared/utils/email';
import { requestInstructorName } from '@/shared/utils/requestInstructor';

/**
 * Who the instructor of record is, and a way to add or change them after the
 * request is filed (PUT /api/application/:id/instructor). Offered to the
 * submitter at any status and to a coordinator of a program the request
 * ranks. The new instructor is told they were named and the one replaced is
 * told they were removed; the history records the change.
 */

const MAX_NAME = 200;

// What the person is told was emailed, in plain words
function savedNote(result, name) {
    if (!result.changed) return `${name} is already the instructor of record.`;
    const told = [
        result.named && 'They were emailed that they were named.',
        result.removed && 'The previous instructor was emailed.',
    ].filter(Boolean);
    return [`${name} is now the instructor of record.`, ...told].join(' ');
}

function InstructorPanel({ application, viewer, onSaved }) {
    const current = requestInstructorName(application, viewer);
    const [editing, setEditing] = useState(false);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [attempted, setAttempted] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [saved, setSaved] = useState(null);
    const nameRef = useRef(null);
    const changeRef = useRef(null);
    const savedRef = useRevealAlert(saved);
    const errorRef = useRevealAlert(error);

    const nameError = !name.trim()
        ? "Enter the instructor's name."
        : name.trim().length > MAX_NAME
          ? `The name can be at most ${MAX_NAME} letters.`
          : null;
    const emailError = !email.trim()
        ? "Enter the instructor's email address."
        : !EMAIL_PATTERN.test(email.trim())
          ? 'Enter a valid email address.'
          : null;

    const open = () => {
        setSaved(null);
        setError(null);
        setAttempted(false);
        setName('');
        setEmail('');
        setEditing(true);
        // Moves once the form is on the page
        setTimeout(() => nameRef.current?.focus(), 0);
    };

    // Cancelling returns focus to the button that opened the form; a save
    // leaves it to the confirmation
    const close = (returnFocus) => {
        setEditing(false);
        if (returnFocus) setTimeout(() => changeRef.current?.focus(), 0);
    };

    const save = async (event) => {
        event.preventDefault();
        setAttempted(true);
        if (nameError || emailError) return;
        setSaving(true);
        setError(null);
        try {
            const result = await applicationService.changeInstructor(
                application._id,
                { name: name.trim(), email: email.trim() }
            );
            onSaved(result.application);
            setSaved(
                savedNote(
                    result,
                    requestInstructorName(result.application, viewer) ||
                        name.trim()
                )
            );
            close(false);
        } catch (err) {
            setError(
                err.response?.data?.message ||
                    "The instructor couldn't be saved. Try again."
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card
            className="border-0 shadow-sm mb-4"
            data-testid="instructor-panel"
        >
            <Card.Body>
                <div className="d-flex flex-wrap align-items-start justify-content-between gap-2">
                    <div>
                        <h2 className="h5 mb-1">Instructor</h2>
                        <p className="mb-0 text-break">
                            {current || 'Not named yet'}
                        </p>
                    </div>
                    {!editing && (
                        <Button
                            ref={changeRef}
                            variant="outline-primary"
                            size="sm"
                            onClick={open}
                        >
                            {current ? 'Change instructor' : 'Add instructor'}
                        </Button>
                    )}
                </div>
                {saved && (
                    <Alert
                        ref={savedRef}
                        tabIndex={-1}
                        variant="success"
                        className="mt-3 mb-0"
                        dismissible
                        onClose={() => setSaved(null)}
                    >
                        {saved}
                    </Alert>
                )}
                {editing && (
                    <Form noValidate onSubmit={save} className="mt-3">
                        <p className="text-muted">
                            The new instructor is emailed that they were named,
                            and gets every email about this request from now on.
                            {current && application.isSubmittedOnBehalf
                                ? ` ${current} is told they were removed and no longer sees the section.`
                                : ''}
                        </p>
                        {error && (
                            <Alert
                                ref={errorRef}
                                tabIndex={-1}
                                variant="danger"
                            >
                                {error}
                            </Alert>
                        )}
                        <Form.Group
                            className="mb-3"
                            controlId="instructor-name"
                        >
                            <Form.Label>Instructor name</Form.Label>
                            <Form.Control
                                ref={nameRef}
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                isInvalid={attempted && !!nameError}
                                required
                                aria-describedby="instructor-name-error"
                            />
                            <Form.Control.Feedback
                                type="invalid"
                                id="instructor-name-error"
                            >
                                {nameError}
                            </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group
                            className="mb-3"
                            controlId="instructor-email"
                        >
                            <Form.Label>Instructor email address</Form.Label>
                            <Form.Control
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                isInvalid={attempted && !!emailError}
                                required
                                aria-describedby="instructor-email-error"
                            />
                            <Form.Control.Feedback
                                type="invalid"
                                id="instructor-email-error"
                            >
                                {emailError}
                            </Form.Control.Feedback>
                        </Form.Group>
                        <ActionBar
                            primary={
                                <Button type="submit" disabled={saving}>
                                    {saving ? 'Saving…' : 'Save instructor'}
                                </Button>
                            }
                            secondary={
                                <Button
                                    variant="outline-primary"
                                    onClick={() => close(true)}
                                    disabled={saving}
                                >
                                    Cancel
                                </Button>
                            }
                        />
                    </Form>
                )}
            </Card.Body>
        </Card>
    );
}

InstructorPanel.propTypes = {
    application: PropTypes.shape({
        _id: PropTypes.string.isRequired,
        isSubmittedOnBehalf: PropTypes.bool,
    }).isRequired,
    viewer: PropTypes.shape({ _id: PropTypes.string, name: PropTypes.string }),
    onSaved: PropTypes.func.isRequired,
};

export default InstructorPanel;
